import { RichTextEditor, Link } from "@mantine/tiptap"
import { useEditor } from "@tiptap/react"
import Highlight from "@tiptap/extension-highlight"
import StarterKit from "@tiptap/starter-kit"
import Underline from "@tiptap/extension-underline"
import TextAlign from "@tiptap/extension-text-align"
import Superscript from "@tiptap/extension-superscript"
import SubScript from "@tiptap/extension-subscript"
import Placeholder from "@tiptap/extension-placeholder"
import TextStyle from "@tiptap/extension-text-style"
import { Color } from "@tiptap/extension-color"
import { createStyles } from "@mantine/core"
import { DEFAULT_LABELS } from "../../shared/lib/constants"
import React, { useEffect } from "react"

const useStyles = createStyles(() => ({
  content: {
    minHeight: 200,
  },
}))

type Props = {
  onChange: (value: string) => void
  placeholder: string
  initialValue: string
}

export function TextEditor({ onChange, initialValue, placeholder }: Props): React.JSX.Element {
  const { classes } = useStyles()

  const editorInst = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link,
      Superscript,
      SubScript,
      Highlight,
      TextStyle,
      Color,
      TextAlign.configure({ types: [ "heading", "paragraph" ] }),
      Placeholder.configure({ placeholder }),
    ],
    content: initialValue,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
  })

  useEffect(() => {
    if (editorInst) {
      editorInst.commands.setContent(initialValue)
    }
  }, [ initialValue, editorInst ])

  return (
    <RichTextEditor editor={editorInst} labels={DEFAULT_LABELS}>
      <RichTextEditor.Toolbar sticky>
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Bold/>
          <RichTextEditor.Italic/>
          <RichTextEditor.Underline/>
          <RichTextEditor.Strikethrough/>
          <RichTextEditor.ClearFormatting/>
          <RichTextEditor.Highlight/>
          <RichTextEditor.Code/>
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <RichTextEditor.H1/>
          <RichTextEditor.H2/>
          <RichTextEditor.H3/>
          <RichTextEditor.H4/>
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Blockquote/>
          <RichTextEditor.Hr/>
          <RichTextEditor.BulletList/>
          <RichTextEditor.OrderedList/>
          <RichTextEditor.Subscript/>
          <RichTextEditor.Superscript/>
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Link/>
          <RichTextEditor.Unlink/>
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <RichTextEditor.AlignLeft/>
          <RichTextEditor.AlignCenter/>
          <RichTextEditor.AlignJustify/>
          <RichTextEditor.AlignRight/>
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ColorPicker
          colors={[
            "#25262b",
            "#868e96",
            "#fa5252",
            "#e64980",
            "#be4bdb",
            "#7950f2",
            "#4c6ef5",
            "#228be6",
            "#15aabf",
            "#12b886",
            "#40c057",
            "#82c91e",
            "#fab005",
            "#fd7e14",
          ]}
        />
      </RichTextEditor.Toolbar>

      <RichTextEditor.Content className={classes.content}/>
    </RichTextEditor>
  )
}
